///
/// EarthBrowser
///
/// Created by Matt Giger on 9/19/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The engine's only seam to redux. Engine code (Clock, EarthActivity,
/// camera state/director, gesture handlers) reads and dispatches through
/// this bridge instead of importing the store, so builds without a store
/// (the /embed globe-only entry) tree-shake the entire redux graph away.
///
/// The default implementation is inert: getState() returns undefined (call
/// sites fall back to their built-in defaults), dispatch/subscribe are
/// no-ops. The app bootstrap installs the real store-backed implementation
/// (src/redux/engineBridgeSetup.ts) before the engine is constructed.
///

import type { RootState } from "@/redux/store"

export interface EngineReduxBridge {
	/** Live redux state, or undefined when no store exists (embed builds) */
	getState(): RootState | undefined
	/** Dispatch a plain action; no-op without a store */
	dispatch(action: { type: string; payload?: unknown }): void
	/** Store subscription (fires on every state change); no-op without a store */
	subscribe(listener: () => void): () => void
	/** Action-type subscription (ReduxActionBridge); no-op without a store */
	subscribeAction<T = unknown>(actionType: string, callback: (payload: T) => void): () => void
}

const inertBridge: EngineReduxBridge = {
	getState: () => undefined,
	dispatch: () => {},
	subscribe: () => () => {},
	subscribeAction: () => () => {},
}

export let engineBridge: EngineReduxBridge = inertBridge

export function installEngineBridge(bridge: EngineReduxBridge): void {
	engineBridge = bridge
}

// Entitlement tier assumed when no store exists. Embeds stay on free-tier
// depth caps; the first-party video render mode (src/embed/renderHooks.ts)
// raises it so a time-lapse can reach the full paid history window.
let paidEntitlementDefault = false

export function setPaidEntitlementDefault(paid: boolean): void {
	paidEntitlementDefault = paid
}

/** Paid tier per the live store, or the store-less default */
export function isPaidEntitlement(): boolean {
	return engineBridge.getState()?.entitlements.isPaid ?? paidEntitlementDefault
}

/**
 * Action type strings the engine dispatches/subscribes to, kept here so the
 * engine never imports slice modules (which pull in the whole redux graph).
 * engineBridgeSetup.ts asserts each one matches its real action creator, so
 * a slice rename fails fast at app startup instead of silently desyncing.
 */
export const engineActions = {
	setClockCurrentTime: "earthbrowser/setClockCurrentTime",
	setClockState: "earthbrowser/setClockState",
	setClockAnimatingSpeed: "earthbrowser/setClockAnimatingSpeed",
	setClockStartOffset: "earthbrowser/setClockStartOffset",
	setClockEndOffset: "earthbrowser/setClockEndOffset",
	setCameraState: "earthbrowser/setCameraState",
	sendDatasetAction: "earthbrowser/sendDatasetAction",
	sendCameraCommand: "earthbrowser/sendCameraCommand",
	setClockSpeedCap: "dialog/setClockSpeedCap",
	setCameraMode: "dialog/setCameraMode",
	setActiveStory: "dialog/setActiveStory",
	addStoryDrawingPoint: "dialog/addStoryDrawingPoint",
	setDropPinMenu: "dialog/setDropPinMenu",
	setWebcamSheet: "dialog/setWebcamSheet",
	setQuakeCutout: "dialog/setQuakeCutout",
	updateQuakeCutout: "dialog/updateQuakeCutout",
	setStormCutout: "dialog/setStormCutout",
	updateStormCutout: "dialog/updateStormCutout",
	setStormWindProbabilityKt: "dialog/setStormWindProbabilityKt",
	setPaidEntitlement: "entitlements/setPaidEntitlement",
	openInfoWindow: "userInterface/openInfoWindow",
	updateSatellitePosition: "userInterface/updateSatellitePosition",
} as const
